import type { KryspaceRenderManifest, RenderManifestQueueItem } from '../../../types/kryspaceRenderManifest';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';

export interface EngineeringWorkspaceLink {
  supplyItemRef: string;
  displayName: string;
  engineeringWorkspaceSlug: string;
  roleSummary: string;
  linkageMode: 'rollup' | 'navigate';
  reachable: boolean;
  unreachableReason?: string;
}

export interface McEngineeringWorkspacesModel {
  parentSlug?: string;
  links: EngineeringWorkspaceLink[];
}

function rowField(row: RenderManifestQueueItem, key: string): unknown {
  return (row as RenderManifestQueueItem & Record<string, unknown>)[key];
}

export function parseMcEngineeringWorkspacesFromManifest(
  manifest: KryspaceRenderManifest
): McEngineeringWorkspacesModel | null {
  const section = manifest.sections.find((s) => s.id === 'engineering-workspaces');
  if (!section?.queue?.length) return null;

  const links: EngineeringWorkspaceLink[] = section.queue.flatMap((row) => {
    const slug =
      (typeof row.workspaceSlug === 'string' && row.workspaceSlug) ||
      (typeof rowField(row, 'engineeringWorkspaceSlug') === 'string'
        ? (rowField(row, 'engineeringWorkspaceSlug') as string)
        : '');
    if (!slug) return [];

    const itemId =
      (typeof row.itemId === 'string' && row.itemId) ||
      (typeof rowField(row, 'item_id') === 'string' ? (rowField(row, 'item_id') as string) : slug);
    const linkageRaw = rowField(row, 'linkageMode');
    const unreachableReason =
      typeof rowField(row, 'unreachableReason') === 'string'
        ? (rowField(row, 'unreachableReason') as string)
        : undefined;

    const link: EngineeringWorkspaceLink = {
      supplyItemRef: itemId,
      displayName: fixUtf8Mojibake(String(row.title || slug)),
      engineeringWorkspaceSlug: slug,
      roleSummary: fixUtf8Mojibake(String(row.statusPhrase || '')),
      linkageMode: linkageRaw === 'navigate' ? 'navigate' : 'rollup',
      reachable: rowField(row, 'reachable') !== false,
      ...(unreachableReason ? { unreachableReason } : {}),
    };
    return [link];
  });

  if (!links.length) return null;

  return {
    parentSlug:
      typeof section.meta?.schemaVersion === 'string'
        ? manifest.workspaceSlug
        : manifest.workspaceSlug,
    links,
  };
}
